import { Avatar, Dropdown, Layout, Badge, Tooltip, Typography } from "antd";
import { useEffect, useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { getNotificationService, getProjectService } from "../../backend";

import Logo from "../../assets/logo-color.svg";
import { useAuth } from "../../contexts/Auth";
import { useRecoilState, useResetRecoilState, useSetRecoilState } from "recoil";
import { projectsState } from "../../recoil/project";
import { User, BellRing, Sparkles, AlertTriangle } from "lucide-react";
import { handleSetMixpanelUserGroups } from "../../backend/tracking";
import ChangelogDrawer from "./ChangelogDrawer";
import { trackNotificationsClicked, trackWhatsNewClicked } from "../../backend/tracking/user-settings";
import NotificationsDrawer from "./NotificationsDrawer";
import { newNotificationsState } from "../../recoil/notification";
import { UserNotificationsNotifications } from "@microtica/ms-notification-sdk";
import { clearSession } from "../../utils/mfa-step-up";
import { canSwitchToNewUI, setNewUI } from "../../v2/flag";
import NewUIButton from "../../components/NewUIButton";

const { Header, Content } = Layout;

const HomePageLayout = () => {
    const badgeColor = "#1890ff";
    const navigate = useNavigate();
    const { profile } = useAuth();
    const { isLoggedIn } = useAuth();
    // recoil project
    const [projects, setProjects] = useRecoilState(projectsState);
    const resetProjectsState = useResetRecoilState(projectsState);
    // recoil notification
    const setNewUserNotifications = useSetRecoilState(newNotificationsState);
    // useState
    const [loadingProjects, setLoadingProjects] = useState(false);
    const [showChangelog, setShowChangelog] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [showAlarmNotifications, setShowAlarmNotifications] = useState(false);
    const [userNotifications, setUserNotifications] = useState([] as UserNotificationsNotifications[]);

    useEffect(() => {
        const loadData = async () => {
            setLoadingProjects(true);
            try {
                const [{ data: { projects } }] = await Promise.all([
                    getProjectService().listProjects()
                ])

                setProjects(projects);
                handleSetMixpanelUserGroups(projects.map(item => item.id));
            } catch (error) {
            } finally {
                setLoadingProjects(false);
            }
        }
        if (isLoggedIn) {
            loadData();
            fetchNotifications();

            const notificationsInterval = setInterval(async () => {
                await fetchNotifications();
            }, 30000);

            return () => {
                clearInterval(notificationsInterval);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    const handleLogout = () => {
        clearSession();
        resetProjectsState();
        navigate("/auth/login");
    }

    const handleOpenChangelog = () => {
        setShowChangelog(true);
        trackWhatsNewClicked();
    }

    const handleOpenNotifications = () => {
        setShowNotifications(true);
        trackNotificationsClicked();
    }

    const handleOpenAlarmNotifications = () => {
        setShowAlarmNotifications(true);
    }

    const fetchNotifications = async () => {
        const { data: { notifications } } = await getNotificationService().getAllUserNotifications();

        // update recoil state only if there are new notifications
        setUserNotifications(oldNotifications => {
            if ((oldNotifications.length === 0 && notifications.length > 0) ||
                (notifications[0]?.id !== oldNotifications[0]?.id || notifications.filter(n => n.isRead).length !== oldNotifications.filter(o => o.isRead).length)) {
                const oldNotificationsIds = oldNotifications.map(oldNotification => oldNotification.id);
                const newNotifications = notifications.filter(notification => !oldNotificationsIds.includes(notification.id));
                setNewUserNotifications(newNotifications);
                return notifications;
            } else {
                return oldNotifications;
            }
        })
    }

    const HeaderActions = () => {
        const updatesCount = userNotifications.filter(notification => !notification.isRead && (notification.entityType as string) !== "MONITORING").length;
        const incidentsCount = userNotifications.filter(notification => !notification.isRead && (notification.entityType as string) === "MONITORING").length;
        
        return (
            <div className="header-actions">
                <NewUIButton />
                <div className="header-actions-group">
                    <Tooltip title="Updates" placement="bottom">
                        <Badge count={updatesCount} size="small" offset={[-2, 2]}>
                            <button
                                onClick={handleOpenNotifications}
                                className="header-icon-btn"
                            >
                                <BellRing size={18} />
                            </button>
                        </Badge>
                    </Tooltip>
                    <Tooltip title="Incidents" placement="bottom">
                        <Badge count={incidentsCount} size="small" offset={[-2, 2]}>
                            <button
                                onClick={handleOpenAlarmNotifications}
                                className="header-icon-btn"
                            >
                                <AlertTriangle size={18} />
                            </button>
                        </Badge>
                    </Tooltip>
                    <Tooltip title="What's new" placement="bottom">
                        <button
                            onClick={handleOpenChangelog}
                            className="header-icon-btn"
                        >
                            <Sparkles size={18} />
                        </button>
                    </Tooltip>
                </div>
                <Dropdown
                    placement="bottomRight"
                    menu={{
                        items: [{
                            key: "profile-email",
                            label: (
                                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                                    {profile?.email}
                                </Typography.Text>
                            ),
                            disabled: true
                        },
                        {
                            type: 'divider'
                        },
                        {
                            key: "account-security",
                            label: "Account security",
                            onClick: () => navigate("/account/security")
                        },
                        ...(canSwitchToNewUI() ? [{
                            key: "new-ui",
                            label: "Try the new Microtica",
                            onClick: () => setNewUI(true, "/projects")
                        }] : []),
                        {
                            key: "logout",
                            label: "Logout",
                            onClick: handleLogout
                        }]
                    }}
                    trigger={['click']}
                >
                    <div className="header-user">
                        <Avatar 
                            size={32}
                            icon={<User size={16} />}
                            className="header-avatar"
                        />
                    </div>
                </Dropdown>
            </div>
        )
    }

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Layout>
                <Header className="header flex-align-center flex-justify-space-between" >
                    <div style={{ margin: "16px 0" }}>
                        <Link to="/projects">
                            <img src={Logo} height="28px" alt="Microtica" />
                        </Link>
                    </div>
                    {
                        isLoggedIn && <HeaderActions />
                    }
                </Header>
                <Content>
                    <Outlet />
                </Content>
            </Layout>
            <ChangelogDrawer visible={showChangelog} onClose={() => setShowChangelog(false)} />
            {
                (showNotifications || showAlarmNotifications) &&
                <NotificationsDrawer
                    projects={projects}
                    notifications={userNotifications.filter(notification => showAlarmNotifications ? (notification.entityType as string) === "MONITORING" : (notification.entityType as string) !== "MONITORING")}
                    visible={showNotifications || showAlarmNotifications}
                    type={showAlarmNotifications ? "Incidents" : "Updates"}
                    onClose={() => {
                        setShowNotifications(false);
                        setShowAlarmNotifications(false);
                        fetchNotifications();
                    }}
                    onMarkAsRead={() => {
                        fetchNotifications();
                    }}
                />
            }
        </Layout>
    );
}

export default HomePageLayout;
